import {Axes, DisplayModes} from "./graph-renderer";
import {ReportData} from "./chart-data";

export const addCallbacks = <FF extends Filters>(
	p: FilterWidgetValueParams,
	callbacks: FilterCallbacks<keyof FF>,
	filterState: FilterState
): FilterWidgetParams<FF> => {
	switch (p.type) {
		case "checkbox":
			return {...p,
				onSetCheckbox: callbacks.onSetCheckbox,
				onUnset: callbacks.onUnsetCheckbox,
				checked: filterState.type == 'checkbox' ? filterState.checked : []
			}
		case "select box":
			return {...p,
				onSelect: callbacks.onSelect,
				selected: filterState.type == 'select box' ? filterState.selected : p.selectedDefault
			}
		case "date range":
			return {
				...p,
				onSetFromDate: callbacks.onSetFromDate,
				onSetToDate: callbacks.onSetToDate,
				fromDate: filterState.type == 'date range'? filterState.fromDate : new Date(),
				toDate: filterState.type == 'date range'? filterState.toDate : new Date()
			}
	}
}

type FilterToggle<F> = (id: F, value: string) => void
type FilterCallbacks<F> = {
	onSetCheckbox: FilterToggle<F>,
	onUnsetCheckbox: FilterToggle<F>
	onSelect: (selected: string, filter: F) => void
	onSetFromDate: (d: Date, filter: F) => void
	onSetToDate: (d: Date, filter: F) => void
}

export type Filters = {[index: string]: FilterWidgetValueParams}

export type FilterProps<FF extends Filters> = {
	id: keyof FF,
	enabled: boolean,
	data: FilterWidgetParams<FF>,
}

export type FilterWidgetParams<FF extends Filters> = {isHidden: boolean} &
	((FilterWidgetValueParamsDateRange & FilterWidgetCallbackParamsDateRange<FF> & FilterWidgetDateRangeState)
	| (FilterWidgetValueParamsSelectBox & FilterWidgetCallbackParamsSelectBox<FF> & FilterWidgetSelectBoxState)
	| (FilterWidgetValueParamsCheckbox & FilterWidgetCallbackParamsCheckbox<FF> & FilterWidgetCheckboxState))

export type FilterState = FilterWidgetDateRangeState
	| FilterWidgetSelectBoxState
	| FilterWidgetCheckboxState

export type FilterWidgetValueParams = {isHidden: boolean} & (FilterWidgetValueParamsDateRange
	| FilterWidgetValueParamsSelectBox
	| FilterWidgetValueParamsCheckbox)

type FilterWidgetCallbackParamsDateRange<FF extends Filters> = {
	type: "date range"
	onSetFromDate: (date: Date, filter: keyof FF) => void
	onSetToDate: (date: Date, filter: keyof FF) => void
}
type FilterWidgetCallbackParamsSelectBox<FF extends Filters> = {
	type: "select box"
	onSelect: (value: string, filter: keyof FF) => void
}
type FilterWidgetCallbackParamsCheckbox<FF extends Filters> = {
	type: "checkbox"
	onSetCheckbox: FilterToggle<keyof FF>
	onUnset: FilterToggle<keyof FF>
}

type FilterWidgetValueParamsDateRange = {
	type: "date range"
}
type FilterWidgetValueParamsSelectBox = {
	type: "select box"
	selectedDefault: string
	options: {[index: string]: string}
}
type FilterWidgetValueParamsCheckbox = {
	type: "checkbox"
	label: string
	group: FilterWidgetCheckboxItem[]
}
export type FilterWidgetCheckboxItem = {value: string, label: string}

type FilterWidgetDateRangeState = {
	type: "date range"
	fromDate: Date
	toDate: Date
}

export type DateFilterKey = "fromDate"|"toDate"

export const dateFromFilter = (filter: FilterState, key: DateFilterKey): Date => filter.type == "date range" ? filter[key] : new Date()

type FilterWidgetSelectBoxState = {
	type: "select box"
	selected: string
}
export type FilterWidgetCheckboxState = {
	type: "checkbox"
	checked: string[]
}
export type CheckboxFilterProps<FF extends Filters> = {
	enabled: boolean,
	label: string,
	id: keyof FF,
	onSetFilter: FilterToggle<keyof FF>,
	onUnsetFilter: FilterToggle<keyof FF>,
	options: FilterWidgetCheckboxItem[]
	checked: string[]
}

export type ReportValueProps<AA extends Axes, FF extends Filters, DD extends DisplayModes, RR extends ReportTypes<AA, FF, DD>> = {
	title: string,
	displayMode: keyof DD,
	displayModes: DD,
	chartData?: ReportData,
	reportType: keyof RR,
	reportTypes: RR,
	filters: FF
	filterState: {[K in keyof FF]: FilterState}
	axes: AA
}

export type ReportPropsCallback<ReportType, F, DD extends DisplayModes> = {
	setDisplay: (newMode: keyof DD) => void,
	runReport: () => void,
	onSetReportType: (type: ReportType) => void,
	filterCallbacks: FilterCallbacks<F>
}

export type ReportTypeKey = {
	id: string
	label: string
}

export type ReportTypes<AA extends Axes, FF extends Filters, DD extends DisplayModes> = {[index: string]: ReportTypeData<AA, FF, DD>}
export type ReportTypeData<AA extends Axes, FF extends Filters, DD extends DisplayModes> = {
	label: string,
	axis: keyof AA,
	filters: (keyof FF)[]
	renderAs: 'pie'|'line'|'bar'
	removeEmptyAxes: boolean
	keys: ReportTypeKey[],
	defaultDisplayMode: keyof DD
	displayModes: (keyof DD)[]
}

export const filterIsEnabled = <FF extends Filters>(filterId: keyof FF, report: ReportTypeData<any, FF, any>) => report.filters.indexOf(filterId) >= 0
export type ReportProps<AA extends Axes, FF extends Filters, DD extends DisplayModes, RR extends ReportTypes<AA, FF, DD>> =
	ReportValueProps<AA, FF, DD, RR> & ReportPropsCallback<keyof RR, keyof FF, DD>



// WEBPACK FOOTER //
// ./src/lib/report-builder/lib2.ts